import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import {
  buildDevBypassUser,
  isAdminFromGroups,
  normalizeCognitoGroups,
  resolvePrimaryRole,
  userFromEntraClaims,
  userFromIdTokenPayload,
} from "@/lib/authRoles";
import {
  authComplete,
  authCompleteNewPassword,
  authConfig,
  authConfirmForgotPassword,
  authForgotPassword,
  authLogin,
  authLogout,
  authRefresh,
  formatAuthApiError,
  isBackendAuthEnabled,
  resolveAuthLoginFailure,
} from "@/lib/authApi";
import { entraLoginUrl, isEntraCallbackPath } from "@/lib/entraAuth";
import { addSessionRefreshListener, shouldProactivelyRefreshAccessToken, silentRefreshBackendSession } from "@/lib/sessionRefresh";
import { cognitoUserPool, isCognitoConfigured, isLocalDevBypassEnabled } from "@/lib/cognitoPool";
import { deleteAssistantSession, readActiveAssistantSessionId } from "@/lib/assistantApi";
import {
  formatCognitoAuthError,
  resolveCognitoSignInFailure,
} from "@/lib/cognitoAuthErrors";
import {
  applyAuthTokens,
  clearCognitoTokens,
  decodeJwtPayload,
  getCognitoAccessToken,
  getCognitoIdToken,
  getIdpRoles,
  getRefreshToken,
  getStoredIdpMode,
  isAccessTokenUsable,
  setAuthEmail,
  setStoredIdpMode,
} from "@/lib/tokenStore";

const IDLE_LOGOUT_MS = 30 * 60 * 1000;

const AuthContext = createContext(null);

function createCognitoUser(email) {
  return new CognitoUser({
    Username: email.trim().toLowerCase(),
    Pool: cognitoUserPool,
  });
}

function userFromAuthApiUser(apiUser) {
  const groups = normalizeCognitoGroups(apiUser?.groups);
  return {
    email: String(apiUser?.email || ""),
    sub: String(apiUser?.sub || ""),
    username: String(apiUser?.username || apiUser?.sub || ""),
    groups,
    role: resolvePrimaryRole(groups),
  };
}

function userFromSession(session) {
  if (!session?.isValid()) {
    clearCognitoTokens();
    return null;
  }
  applyAuthTokens({
    access_token: session.getAccessToken().getJwtToken(),
    id_token: session.getIdToken().getJwtToken(),
    refresh_token: session.getRefreshToken()?.getToken(),
  });
  return userFromIdTokenPayload(session.getIdToken().decodePayload());
}

function userFromStoredIdToken() {
  const idToken = getCognitoIdToken();
  if (!idToken) return null;
  try {
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    return userFromIdTokenPayload(payload);
  } catch {
    return null;
  }
}

function userFromStoredEntra() {
  const accessToken = getCognitoAccessToken();
  if (!isAccessTokenUsable(accessToken)) return null;
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return null;
  return userFromEntraClaims(payload, getIdpRoles());
}

function filterChallengeAttributes(userAttributes) {
  if (!userAttributes) return {};
  const skip = new Set([
    "sub",
    "username",
    "email",
    "email_verified",
    "phone_number",
    "phone_number_verified",
    "identities",
    "picture",
    "nickname",
    "preferred_username",
  ]);
  const out = {};
  for (const [key, value] of Object.entries(userAttributes)) {
    if (skip.has(key)) continue;
    if (value === undefined || value === null || value === "") continue;
    out[key] = value;
  }
  return out;
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idpMode, setIdpMode] = useState(null);
  const [idpUnavailable, setIdpUnavailable] = useState(false);
  const pendingNewPasswordRef = useRef(null);

  const useBackendAuth = isBackendAuthEnabled();

  const groups = user?.groups ?? [];
  const role = user?.role ?? null;
  const isAdmin = useMemo(() => isAdminFromGroups(groups), [groups]);

  const clearAuth = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    clearCognitoTokens();
  }, []);

  const applyBackendTokens = useCallback((data) => {
    applyAuthTokens(data);
    if (data.user?.email || data.user?.sub) {
      if (data.user?.email) {
        setAuthEmail(data.user.email);
      }
      setUser(userFromAuthApiUser(data.user));
      setIsAuthenticated(true);
      return;
    }
    if (Array.isArray(data.roles) && data.access_token) {
      const payload = decodeJwtPayload(data.access_token) || {};
      const nextUser = userFromEntraClaims(payload, data.roles);
      if (nextUser.email) {
        setAuthEmail(nextUser.email);
      }
      setUser(nextUser);
      setIsAuthenticated(true);
      return;
    }
    setIsAuthenticated(true);
  }, []);

  const refreshFromEntra = useCallback(async () => {
    const restored = userFromStoredEntra();
    if (restored) {
      setUser(restored);
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }
    try {
      const data = await authRefresh();
      applyBackendTokens(data);
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [applyBackendTokens, clearAuth]);

  const refreshFromBackend = useCallback(async () => {
    const accessToken = getCognitoAccessToken();
    if (isAccessTokenUsable(accessToken) && getCognitoIdToken()) {
      const restored = userFromStoredIdToken();
      if (restored) {
        setUser(restored);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      const restored = userFromStoredIdToken();
      if (restored) {
        setUser(restored);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }
      clearAuth();
      setLoading(false);
      return;
    }
    try {
      const data = await authRefresh(refreshToken);
      applyBackendTokens(data);
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [applyBackendTokens, clearAuth]);

  const applyIdpConfig = useCallback((mode) => {
    const previous = getStoredIdpMode();
    const onEntraReturn = typeof window !== "undefined" && isEntraCallbackPath();
    if (previous && mode && previous !== mode && !onEntraReturn) {
      clearCognitoTokens();
      setIsAuthenticated(false);
      setUser(null);
    }
    if (mode) {
      setStoredIdpMode(mode);
    }
    setIdpMode(mode);
    setIdpUnavailable(false);
  }, []);

  const refreshFromPool = useCallback(() => {
    if (isLocalDevBypassEnabled()) {
      setUser(buildDevBypassUser());
      setIsAuthenticated(true);
      clearCognitoTokens();
      setLoading(false);
      return;
    }
    if (!cognitoUserPool) {
      setLoading(false);
      clearAuth();
      return;
    }
    const cognitoUser = cognitoUserPool.getCurrentUser();
    if (!cognitoUser) {
      clearAuth();
      setLoading(false);
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        clearAuth();
        setLoading(false);
        return;
      }
      setUser(userFromSession(session));
      setIsAuthenticated(true);
      setLoading(false);
    });
  }, [clearAuth]);

  const loadBackendSession = useCallback(async () => {
    setIdpUnavailable(false);
    try {
      const cfg = await authConfig();
      const mode = cfg?.idp === "entra" ? "entra" : cfg?.idp === "cognito" ? "cognito" : null;
      if (!mode) {
        setIdpUnavailable(true);
        clearAuth();
        setLoading(false);
        return;
      }
      applyIdpConfig(mode);
      if (typeof window !== "undefined" && isEntraCallbackPath()) {
        setLoading(false);
        return;
      }
      if (mode === "entra") {
        await refreshFromEntra();
        return;
      }
      await refreshFromBackend();
    } catch {
      // Pre-dual-IdP gateways lack GET /api/auth/config (403 MissingAuthenticationToken).
      // Fall back to Cognito pool + BFF login when pool IDs are in config.js.
      if (isCognitoConfigured) {
        applyIdpConfig("cognito");
        await refreshFromPool();
        return;
      }
      setIdpUnavailable(true);
      setIdpMode(null);
      clearAuth();
      setLoading(false);
    }
  }, [applyIdpConfig, clearAuth, refreshFromBackend, refreshFromEntra, refreshFromPool]);

  useEffect(() => {
    return addSessionRefreshListener((data) => {
      applyBackendTokens(data);
    });
  }, [applyBackendTokens]);

  useEffect(() => {
    if (isLocalDevBypassEnabled() || !useBackendAuth) {
      refreshFromPool();
      return;
    }
    loadBackendSession();
  }, [loadBackendSession, refreshFromPool, useBackendAuth]);

  useEffect(() => {
    if (!isAuthenticated || !useBackendAuth || isLocalDevBypassEnabled()) {
      return undefined;
    }
    const tick = () => {
      if (shouldProactivelyRefreshAccessToken()) {
        silentRefreshBackendSession();
      }
    };
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [isAuthenticated, useBackendAuth]);

  const login = useCallback(
    async (email, password) => {
      if (isLocalDevBypassEnabled()) {
        setUser(buildDevBypassUser(email));
        setIsAuthenticated(true);
        clearCognitoTokens();
        return { success: true };
      }
      if (useBackendAuth) {
        try {
          const data = await authLogin({ email, password });
          if (data.challenge === "NEW_PASSWORD_REQUIRED") {
            pendingNewPasswordRef.current = {
              email: data.email || email.trim().toLowerCase(),
              session: data.session,
              userAttributes: filterChallengeAttributes(data.user_attributes),
            };
            return { success: false, newPasswordRequired: true };
          }
          applyBackendTokens(data);
          return { success: true };
        } catch (err) {
          clearCognitoTokens();
          const failure = resolveAuthLoginFailure(err);
          return {
            success: false,
            error: failure.message || formatAuthApiError(err),
            accountLocked: failure.accountLocked,
            lockoutUntil: failure.lockoutUntil ?? null,
          };
        }
      }
      if (!cognitoUserPool) {
        return {
          success: false,
          error: "Authentication is not configured (set REACT_APP_BACKEND_URL).",
        };
      }
      return new Promise((resolve) => {
        const authDetails = new AuthenticationDetails({
          Username: email.trim().toLowerCase(),
          Password: password,
        });
        const cognitoUser = new CognitoUser({
          Username: email.trim().toLowerCase(),
          Pool: cognitoUserPool,
        });
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            setUser(userFromSession(session));
            setIsAuthenticated(true);
            resolve({ success: true });
          },
          onFailure: (err) => {
            clearCognitoTokens();
            const failure = resolveCognitoSignInFailure(err);
            resolve({
              success: false,
              error: failure.message,
              lockoutUntil: failure.lockoutUntil,
            });
          },
          newPasswordRequired: (userAttributes) => {
            pendingNewPasswordRef.current = {
              cognitoUser,
              userAttributes: filterChallengeAttributes(userAttributes),
            };
            resolve({ success: false, newPasswordRequired: true });
          },
        });
      });
    },
    [applyBackendTokens, useBackendAuth],
  );

  const completeNewPassword = useCallback(
    async (newPassword) => {
      const pending = pendingNewPasswordRef.current;
      if (useBackendAuth) {
        if (!pending?.session) {
          return {
            success: false,
            error: "Session expired. Sign in again with your temporary password.",
          };
        }
        try {
          const data = await authCompleteNewPassword({
            email: pending.email,
            session: pending.session,
            newPassword,
            userAttributes: pending.userAttributes,
          });
          pendingNewPasswordRef.current = null;
          applyBackendTokens(data);
          return { success: true };
        } catch (err) {
          return { success: false, error: formatAuthApiError(err) };
        }
      }
      if (!pending?.cognitoUser) {
        return {
          success: false,
          error: "Session expired. Sign in again with your temporary password.",
        };
      }
      const { cognitoUser, userAttributes } = pending;
      return new Promise((resolve) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
          onSuccess: (session) => {
            pendingNewPasswordRef.current = null;
            setUser(userFromSession(session));
            setIsAuthenticated(true);
            resolve({ success: true });
          },
          onFailure: (err) => {
            resolve({
              success: false,
              error: err.message || "Could not set new password",
            });
          },
        });
      });
    },
    [applyBackendTokens, useBackendAuth],
  );

  const cancelNewPasswordChallenge = useCallback(() => {
    pendingNewPasswordRef.current = null;
  }, []);

  const forgotPassword = useCallback(
    async (email) => {
      if (isLocalDevBypassEnabled()) {
        return {
          success: false,
          error: "Password reset is not available in local demo mode.",
        };
      }
      const e = email.trim().toLowerCase();
      if (!e) {
        return { success: false, error: "Enter your email address." };
      }
      if (useBackendAuth) {
        try {
          await authForgotPassword(e);
          return { success: true };
        } catch (err) {
          return { success: false, error: formatAuthApiError(err) };
        }
      }
      if (!cognitoUserPool) {
        return { success: false, error: "Authentication is not configured." };
      }
      return new Promise((resolve) => {
        createCognitoUser(e).forgotPassword({
          onSuccess: () => resolve({ success: true }),
          onFailure: (err) => {
            resolve({
              success: false,
              error: formatCognitoAuthError(err, "forgotPassword"),
            });
          },
        });
      });
    },
    [useBackendAuth],
  );

  const confirmForgotPassword = useCallback(
    async (email, code, newPassword) => {
      const e = email.trim().toLowerCase();
      const verificationCode = code.trim();
      if (!verificationCode) {
        return { success: false, error: "Enter the verification code from your email." };
      }
      if (useBackendAuth) {
        try {
          await authConfirmForgotPassword({
            email: e,
            code: verificationCode,
            newPassword,
          });
          return { success: true };
        } catch (err) {
          return { success: false, error: formatAuthApiError(err) };
        }
      }
      if (!cognitoUserPool) {
        return { success: false, error: "Authentication is not configured." };
      }
      return new Promise((resolve) => {
        createCognitoUser(e).confirmPassword(verificationCode, newPassword, {
          onSuccess: () => resolve({ success: true }),
          onFailure: (err) => {
            resolve({
              success: false,
              error: formatCognitoAuthError(err, "resetPassword"),
            });
          },
        });
      });
    },
    [useBackendAuth],
  );

  const signUp = useCallback((email, password) => {
    return new Promise((resolve) => {
      if (!cognitoUserPool) {
        resolve({
          success: false,
          error: "Self-service sign-up is not available.",
        });
        return;
      }
      const e = email.trim().toLowerCase();
      cognitoUserPool.signUp(
        e,
        password,
        [new CognitoUserAttribute({ Name: "email", Value: e })],
        null,
        (err, result) => {
          if (err) {
            resolve({ success: false, error: err.message || "Sign up failed" });
            return;
          }
          resolve({
            success: true,
            userConfirmed: result.userConfirmed,
          });
        },
      );
    });
  }, []);

  const logout = useCallback(() => {
    pendingNewPasswordRef.current = null;
    // Fire-and-forget: wipe the info-assistant's stored data before the auth
    // token goes away. Best-effort — a failed delete must never block logout.
    const assistantSessionId = readActiveAssistantSessionId();
    if (assistantSessionId) {
      deleteAssistantSession(assistantSessionId).catch(() => {});
    }
    if (useBackendAuth) {
      authLogout();
    } else {
      const u = cognitoUserPool?.getCurrentUser();
      if (u) {
        u.signOut();
      }
    }
    clearAuth();
  }, [clearAuth, useBackendAuth]);

  useEffect(() => {
    if (!isAuthenticated || isLocalDevBypassEnabled()) return undefined;
    let timer;
    const resetIdleTimer = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        logout();
      }, IDLE_LOGOUT_MS);
    };
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    }
    resetIdleTimer();
    return () => {
      window.clearTimeout(timer);
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, resetIdleTimer);
      }
    };
  }, [isAuthenticated, logout]);

  const refreshSession = useCallback(async () => {
    if (isLocalDevBypassEnabled()) {
      refreshFromPool();
      return { success: true };
    }
    if (useBackendAuth) {
      const entra = idpMode === "entra";
      if (!entra && !getRefreshToken()) {
        return { success: false, error: "Not signed in." };
      }
      try {
        const data = await authRefresh(entra ? undefined : getRefreshToken());
        applyBackendTokens(data);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: formatAuthApiError(err) || "Could not refresh session.",
        };
      }
    }
    if (!cognitoUserPool) {
      return { success: false, error: "Authentication is not configured." };
    }
    return new Promise((resolve) => {
      const cognitoUser = cognitoUserPool.getCurrentUser();
      if (!cognitoUser) {
        resolve({ success: false, error: "Not signed in." });
        return;
      }
      cognitoUser.getSession((err, session) => {
        if (err || !session?.isValid()) {
          resolve({ success: false, error: "Session expired. Sign in again." });
          return;
        }
        cognitoUser.refreshSession(session.getRefreshToken(), (refreshErr, newSession) => {
          if (refreshErr || !newSession?.isValid()) {
            resolve({
              success: false,
              error: refreshErr?.message || "Could not refresh session.",
            });
            return;
          }
          setUser(userFromSession(newSession));
          setIsAuthenticated(true);
          resolve({ success: true });
        });
      });
    });
  }, [applyBackendTokens, idpMode, refreshFromPool, useBackendAuth]);

  const beginEntraLogin = useCallback(() => {
    window.location.assign(entraLoginUrl());
  }, []);

  const completeEntraHandoff = useCallback(async () => {
    try {
      const data = await authComplete();
      applyBackendTokens(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: formatAuthApiError(err) };
    }
  }, [applyBackendTokens]);

  const isAuthConfigured = useBackendAuth || isCognitoConfigured;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        groups,
        role,
        isAdmin,
        loading,
        idpMode,
        idpUnavailable,
        login,
        signUp,
        forgotPassword,
        confirmForgotPassword,
        completeNewPassword,
        cancelNewPasswordChallenge,
        beginEntraLogin,
        completeEntraHandoff,
        logout,
        refreshSession,
        isCognitoConfigured: isAuthConfigured,
        isLocalDevBypassEnabled,
        isBackendAuthEnabled: useBackendAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
